import React from "react";
import {faMinus, faPlus, faTrash} from "@fortawesome/pro-solid-svg-icons";
import {Styled} from "./Stepper.styles";

type StepperProps = {
    value: number
    max?: number
    min?: number
    size?: 'sm' | 'md'
    /** Stretches to fill its slot — used where it swaps with a full-width button. */
    fullWidth?: boolean
    /** Short unit shown next to the number, e.g. `szt.`. */
    unit?: string
    /** Renders a bin instead of a minus once the value reaches `min`. */
    removeAtMin?: boolean
    decreaseLabel?: string
    increaseLabel?: string
    onDecrease: (event: React.MouseEvent) => void
    onIncrease: (event: React.MouseEvent) => void
}

/**
 * Quantity control used on product cards, the product page and the basket.
 */
const Stepper = (
    {
        value,
        max,
        min = 1,
        size = 'md',
        fullWidth = false,
        unit,
        removeAtMin = false,
        decreaseLabel,
        increaseLabel,
        onDecrease,
        onIncrease
    }: StepperProps) => {

    const atMax = max !== undefined && value >= max
    const atMin = value <= min

    return (
        <Styled.Holder
            $size={size}
            $fullWidth={fullWidth}
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
            <Styled.StepButton
                type={'button'}
                title={decreaseLabel}
                aria-label={decreaseLabel}
                onClick={onDecrease}
            >
                <Styled.StepIcon icon={removeAtMin && atMin ? faTrash : faMinus}/>
            </Styled.StepButton>
            <Styled.Value aria-live={'polite'}>
                {/* Re-keyed on every change so the number pops instead of jumping. */}
                <Styled.ValueNumber
                    key={value}
                    initial={{opacity: 0, y: 5}}
                    animate={{opacity: 1, y: 0}}
                    transition={{type: 'spring', stiffness: 620, damping: 26}}
                >
                    {value}
                </Styled.ValueNumber>
                {unit && (<Styled.ValueUnit>{unit}</Styled.ValueUnit>)}
            </Styled.Value>
            <Styled.StepButton
                type={'button'}
                title={increaseLabel}
                aria-label={increaseLabel}
                disabled={atMax}
                onClick={atMax ? undefined : onIncrease}
            >
                <Styled.StepIcon icon={faPlus}/>
            </Styled.StepButton>
        </Styled.Holder>
    )
}

export default Stepper
